import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import {
  canManage as roleCanManage,
  canSetRates as roleCanSetRates,
  canSeeAll as roleCanSeeAll,
  type AuthConfig,
  type AuthPayload,
  type AuthUser,
  type Client,
  type ClientKind,
  type DayKey,
  type Entry,
  type ID,
  type Member,
  type Project,
  type Role,
  type RunningTimer,
  type Screen,
  type Workspace,
} from '../lib/types'
import {
  ApiError,
  MFA_REQUIRED,
  api,
  type EntryInput,
  type ExportGroupKey,
  type ImportDecision,
  type ImportReport,
  type RestoreReport,
} from '../lib/api'
import { supabase } from '../lib/supabase'
import {
  DAY_SEC,
  MAX_SESSION_SEC,
  dayKey,
  instantOf,
  roundToMinute,
  todayKey,
  weekKeys,
} from '../lib/time'
import { applyAccent, downloadFile } from '../lib/util'

const LAST_WS_KEY = 'cadence.lastWorkspace'
const POLL_MS = 20000

/* ------------------------------------------------------------------ */
/* UI-only state                                                       */
/* ------------------------------------------------------------------ */

export interface Draft {
  desc: string
  projectId: ID | null
  task: string
  billable: boolean
}

export type Modal =
  | { kind: 'project'; project?: Project }
  | { kind: 'client'; client?: Client }
  | { kind: 'invite' }
  | { kind: 'member'; member: Member }
  /*
   * `scope` is which screen opened it -- Timer or Prospects -- and it is
   * required, so the pickers inside cannot quietly offer both populations.
   */
  | { kind: 'entry'; scope: ClientKind; entry?: Entry; day?: DayKey }
  | { kind: 'profile' }
  | {
      kind: 'confirm'
      title: string
      body: string
      cta: string
      danger?: boolean
      /**
       * Make them type this before the button works.
       *
       * For the few actions that cannot be undone by clicking again. Reaching
       * for it is a statement that the thing is irreversible -- so anything
       * carrying it should genuinely be.
       */
      requireText?: string
      onConfirm: () => void
    }
  | null

const EMPTY_DRAFT: Draft = { desc: '', projectId: null, task: '', billable: true }

/* ------------------------------------------------------------------ */

interface AppValue {
  /* session */
  booting: boolean
  auth: AuthPayload | null
  user: AuthUser | null
  authConfig: AuthConfig
  signIn: (email: string, password: string) => Promise<string | null>
  /** True when the password was right but a TOTP code is still outstanding. */
  mfaPending: boolean
  /** Finish a sign-in that stopped at the second factor. */
  submitMfaCode: (code: string) => Promise<string | null>
  signUp: (o: { email: string; name: string; password: string }) => Promise<string | null>
  signOut: () => Promise<void>
  refreshAuth: () => Promise<void>
  /** Resolves with a reason when the mail could not be sent, else null. */
  resetPassword: (email: string) => Promise<string | null>
  /** True when this session came from a reset or invite link. */
  recovery: boolean

  /* workspace selection */
  wsId: ID | null
  selectWorkspace: (id: ID) => void
  createWorkspace: (name: string) => Promise<string | null>
  joinWorkspace: (code: string) => Promise<string | null>
  acceptInvite: (inviteId: ID) => Promise<string | null>

  /* current workspace */
  ws: Workspace | null
  me: Member | null
  role: Role | undefined
  canManage: boolean
  canSeeAll: boolean
  /** Owner, admin or manager -- the same set the database calls is_admin(). */
  canSetRates: boolean
  running: RunningTimer | null
  loadError: string | null

  /* ui */
  screen: Screen
  setScreen: (s: Screen) => void
  draft: Draft
  setDraft: (d: Partial<Draft>) => void
  manualMode: boolean
  setManualMode: (v: boolean) => void
  modal: Modal
  setModal: (m: Modal) => void
  confirm: (o: {
    title: string
    body: string
    cta: string
    danger?: boolean
    /** Demand this phrase be typed first. For actions a second click cannot undo. */
    requireText?: string
    onConfirm: () => void
  }) => void
  toast: string | null
  flash: (msg: string) => void
  busy: boolean

  /* lookups */
  proj: (id: ID | null | undefined) => Project | null
  client: (id: ID | null | undefined) => Client | null
  member: (id: ID | null | undefined) => Member | null
  myWeek: (offset: number) => DayKey[]

  /* mutations */
  act: Actions
}

/** What the report filter bar narrows an export to. */
/** What an invite produced. The link matters most when the email did not arrive. */
export interface InviteResult {
  ok: boolean
  actionLink: string | null
  emailed: boolean
  problem: string | null
  /** Why the mail did not go out, when the server said. */
  emailProblem: string | null
  /** Why no link could be made, when that is what went wrong. */
  linkProblem: string | null
}

export interface ExportFilters {
  userId?: ID | null
  /** Several people. Takes precedence over userId; empty means everyone. */
  userIds?: readonly ID[] | null
  clientId?: ID | null
  projectId?: ID | null
  /** Several clients or projects. Takes precedence; empty means all of them. */
  clientIds?: readonly ID[] | null
  projectIds?: readonly ID[] | null
  billable?: boolean | null
  /** Narrow to these pricing models. Used by the "Fixed fee only" filter. */
  pricingModels?: readonly string[] | null
  /** 'summary' produces the grouped layout finance reads. */
  layout?: 'detailed' | 'summary'
  groupBy?: ExportGroupKey[]
  /** 'prospect' exports prospect time and nothing else. Omitted means clients. */
  kind?: ClientKind
}

/** Every action resolves true on success; failures surface as a toast. */
export interface Actions {
  /**
   * `projectId` overrides the draft's.
   *
   * The draft is state, so a project created a moment ago is not in the one
   * this closure captured. Prospect time needs exactly that: its project is
   * made on demand and used in the same click.
   */
  startTimer: (fixedAmount?: number | null, projectId?: ID | null) => Promise<boolean>
  stopTimer: () => Promise<boolean>
  /**
   * Stop someone else's timer. Owner, admin and manager only.
   *
   * For the one somebody left running on a laptop they closed on Friday: the
   * ten-hour limit is enforced when a timer STOPS, and nothing stops one
   * while its owner is away, so until this existed nobody could end it.
   */
  stopTimerFor: (userId: ID, name: string) => Promise<boolean>
  updateTimer: (patch: Partial<RunningTimer>) => Promise<boolean>
  addManual: (o: {
    start: number
    end: number
    day: DayKey
    /** A flat fee set as the entry is written, instead of after it. */
    fixedAmount?: number | null
    /** Overrides the draft's project. See startTimer. */
    projectId?: ID | null
  }) => Promise<boolean>
  continueEntry: (e: Entry) => Promise<boolean>

  createEntry: (e: EntryInput) => Promise<boolean>
  updateEntry: (id: ID, patch: Partial<EntryInput>) => Promise<boolean>
  deleteEntry: (id: ID) => Promise<boolean>
  duplicateEntry: (e: Entry) => Promise<boolean>
  wipeEntries: (scope: 'mine' | 'all') => Promise<boolean>

  setCell: (projectId: ID, day: DayKey, seconds: number) => Promise<boolean>
  clearRow: (projectId: ID, days: DayKey[]) => Promise<boolean>
  copyWeek: (from: DayKey[], to: DayKey[]) => Promise<boolean>
  submitWeek: (weekStart: DayKey) => Promise<boolean>
  unsubmitWeek: (weekStart: DayKey) => Promise<boolean>

  createProject: (b: Record<string, unknown>) => Promise<boolean>
  updateProject: (id: ID, b: Record<string, unknown>) => Promise<boolean>
  deleteProject: (id: ID) => Promise<boolean>

  createClient: (b: { name: string; contact: string }) => Promise<boolean>
  updateClient: (id: ID, b: Record<string, unknown>) => Promise<boolean>
  /** Send a client to the bin. Reversible, and the only delete the list offers. */
  binClient: (id: ID) => Promise<boolean>
  /** Take one back out, exactly as it was. The name is for the clash message. */
  restoreClient: (id: ID, name: string) => Promise<boolean>
  /** Delete for good, from the bin. Owner, admin and manager; not reversible. */
  purgeClient: (id: ID) => Promise<boolean>

  invite: (b: { email: string; role: Role; capacity: number }) => Promise<InviteResult>
  cancelInvite: (inviteId: ID) => Promise<boolean>
  updateMember: (userId: ID, b: { role?: Role; capacity?: number; billable?: boolean }) => Promise<boolean>
  removeMember: (userId: ID) => Promise<boolean>
  /** Owner/admin. Brings a former member back, with their history intact. */
  restoreMember: (userId: ID) => Promise<boolean>
  /** A one-time password link to pass on, or null if one could not be made. */
  resetLinkFor: (userId: ID) => Promise<string | null>

  updateWorkspace: (b: {
    name?: string
    settings?: Record<string, unknown>
    acceptsSignups?: boolean
  }) => Promise<boolean>
  updateAppearance: (b: Record<string, unknown>) => Promise<boolean>
  regenerateCode: () => Promise<boolean>
  deleteWorkspace: () => Promise<boolean>
  leaveWorkspace: () => Promise<boolean>

  addTag: (name: string) => Promise<boolean>
  removeTag: (name: string) => Promise<boolean>

  /** Preview writes nothing; commit refreshes the workspace afterwards. */
  importCsv: (q: {
    csv: string
    filename: string
    mode: 'preview' | 'commit'
    mapping?: Record<string, ImportDecision>
  }) => Promise<ImportReport>

  exportCsv: (from: DayKey, to: DayKey, scope: 'mine' | 'all', filters?: ExportFilters) => void
  /** Owner only. Downloads a full copy of the workspace. */
  backupWorkspace: (scope?: 'standard' | 'complete') => Promise<boolean>
  /** Owner only. Merges a backup in; resolves with what it did, or null on failure. */
  restoreWorkspace: (file: unknown) => Promise<RestoreReport | null>
}

const Ctx = createContext<AppValue | null>(null)

export function useApp(): AppValue {
  const v = useContext(Ctx)
  if (!v) throw new Error('useApp must be used inside <AppProvider>')
  return v
}

/** For screens, which only render once a workspace is loaded. */
export function useWorkspace(): AppValue & { ws: Workspace; me: Member } {
  const v = useApp()
  if (!v.ws || !v.me) throw new Error('No workspace — screen rendered without one')
  return v as AppValue & { ws: Workspace; me: Member }
}

/* ------------------------------------------------------------------ */

/**
 * The wall clock, kept apart from everything else.
 *
 * It used to live in the same context value as the workspace, the session and
 * every action -- a value rebuilt on each render, ticking once a second. So
 * every component in the app re-rendered every second whether or not it had
 * any use for the time: the timesheet re-ran its per-cell filtering, reports
 * rebuilt their chart geometry, and the team grid re-rendered even though it
 * keeps its own tick precisely to avoid that.
 *
 * On its own, only the two clocks that read it pay for it. `children` is the
 * same element tree each time this re-renders, so React does not walk into
 * it; the tick reaches useNow() subscribers and stops there.
 */
const NowContext = createContext<number>(Date.now())

function NowProvider({ children }: { children: ReactNode }) {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [])

  return <NowContext.Provider value={now}>{children}</NowContext.Provider>
}

/** Milliseconds since the epoch, refreshed every second. */
export function useNow(): number {
  return useContext(NowContext)
}

export function AppProvider({ children }: { children: ReactNode }) {
  const [booting, setBooting] = useState(true)
  // A reset or invite link signs the person in before they have chosen a
  // password. Supabase announces that with PASSWORD_RECOVERY, and until it is
  // dealt with the app must not let them past the set-password screen.
  const [mfaPending, setMfaPending] = useState(false)
  const [recovery, setRecovery] = useState(
    /*
     * Collapsed slashes on purpose. The email template builds this URL from
     * Supabase SiteURL, which carries a trailing slash, so the link arrives
     * as //set-password -- a path an exact comparison rejects, landing the
     * person in the app instead of on the screen that lets them choose a
     * password.
     */
    () => window.location.pathname.split('/').filter(Boolean).join('/') === 'set-password',
  )
  const [auth, setAuth] = useState<AuthPayload | null>(null)
  const [authConfig, setAuthConfig] = useState<AuthConfig>({
    google: false,
    googleDomain: null,
    passwordLogin: true,
  })
  const [wsId, setWsId] = useState<ID | null>(() => {
    try {
      return localStorage.getItem(LAST_WS_KEY)
    } catch {
      return null
    }
  })
  const [ws, setWs] = useState<Workspace | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const [screen, setScreen] = useState<Screen>('timer')
  const [draft, setDraftState] = useState<Draft>(EMPTY_DRAFT)
  const [manualMode, setManualMode] = useState(false)
  const [modal, setModal] = useState<Modal>(null)
  const [toast, setToast] = useState<string | null>(null)
  const toastTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const flash = useCallback((msg: string) => {
    clearTimeout(toastTimer.current)
    setToast(msg)
    toastTimer.current = setTimeout(() => setToast(null), 2800)
  }, [])
  useEffect(() => () => clearTimeout(toastTimer.current), [])

  const confirm = useCallback<AppValue['confirm']>((o) => setModal({ kind: 'confirm', ...o }), [])
  const setDraft = useCallback((d: Partial<Draft>) => setDraftState((s) => ({ ...s, ...d })), [])

  /**
   * What the bar goes back to when an entry is finished.
   *
   * Billable on the client screens, not on Prospects. Chasing work nobody
   * has agreed to pay for is not chargeable time, and defaulting it the
   * other way would put unwon hours in front of whoever reads the billable
   * total -- which is the one figure this app exists to get right.
   *
   * Read from `screen` rather than passed in, because both resets below sit
   * in actions the screens call without knowing which one they are.
   */
  const freshDraft = useCallback(
    (): Draft => ({ ...EMPTY_DRAFT, billable: screen !== 'prospects' }),
    [screen],
  )

  const me = useMemo(
    () => (ws ? ws.team.find((m) => m.id === ws.myUserId) ?? null : null),
    [ws],
  )
  const role = ws?.myRole
  const canManage = roleCanManage(role)
  const canSeeAll = roleCanSeeAll(role)
  const canSetRates = roleCanSetRates(role)
  const running = (ws && me ? ws.running[me.id] : null) ?? null

  /* ---- accent ---- */
  useEffect(() => {
    applyAccent(ws?.appearance.accent ?? '#C24A26')
  }, [ws?.appearance.accent])

  /* ---- boot: who am I? ---- */
  const refreshAuth = useCallback(async () => {
    try {
      /*
       * Ask for the second factor before loading anything.
       *
       * The database now gives a session with an unused factor no role in
       * any workspace, so going straight to me() would render an empty app
       * -- no projects, no team, no explanation -- instead of the prompt
       * that would fix it. This is also the reload path, not just sign-in:
       * refreshing the page must land on the same prompt.
       */
      const { data: session } = await supabase.auth.getSession()
      if (session.session && (await api.mfa.stepUpRequired())) {
        setMfaPending(true)
        setAuth(null)
        return
      }

      setMfaPending(false)
      setAuth(await api.me())
    } catch (err) {
      // Being signed out is the ordinary case and says itself. Anything else
      // is a failure to reach the server, and dumping somebody back to the
      // sign-in screen without a word makes a network blip look like an
      // expired session.
      setAuth(null)
      if (!(err instanceof ApiError) || err.status !== 401) {
        flash(err instanceof Error ? err.message : 'Could not reach the server.')
      }
    }
  }, [flash])

  useEffect(() => {
    void (async () => {
      // A failed Google round trip comes back as ?authError=… on the app root.
      const params = new URLSearchParams(window.location.search)
      const authError = params.get('authError')
      if (authError) {
        flash(authError)
        window.history.replaceState({}, '', window.location.pathname)
      }
      const [, cfg] = await Promise.all([
        refreshAuth(),
        api.config().catch(() => null),
      ])
      if (cfg) setAuthConfig(cfg)
      setBooting(false)
    })()
  }, [refreshAuth, flash])

  /* ---- pick a workspace once we know the memberships ---- */
  useEffect(() => {
    if (!auth) {
      setWs(null)
      return
    }
    const ids = auth.workspaces.map((w) => w.id)
    if (wsId && ids.includes(wsId)) return
    const next = ids[0] ?? null
    setWsId(next)
    if (!next) setWs(null)
  }, [auth, wsId])

  useEffect(() => {
    try {
      if (wsId) localStorage.setItem(LAST_WS_KEY, wsId)
      else localStorage.removeItem(LAST_WS_KEY)
    } catch {
      /* storage blocked — the app still works, it just won't remember */
    }
  }, [wsId])

  /* ---- load the selected workspace ---- */
  useEffect(() => {
    if (!auth || !wsId) return
    let cancelled = false
    void (async () => {
      try {
        const snap = await api.workspace(wsId)
        if (!cancelled) {
          setWs(snap)
          setLoadError(null)
        }
      } catch (err) {
        if (cancelled) return
        if (err instanceof ApiError && err.status === 401) {
          setAuth(null)
          return
        }
        setLoadError(err instanceof Error ? err.message : 'Could not load the workspace.')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [auth, wsId])

  /* ---- background refresh, so teammates' time shows up ---- */
  useEffect(() => {
    if (!wsId || !auth) return
    const id = setInterval(() => {
      if (document.visibilityState !== 'visible') return
      void api
        .workspace(wsId)
        .then(setWs)
        .catch(() => {
          /* transient — the next tick tries again */
        })
    }, POLL_MS)
    return () => clearInterval(id)
  }, [wsId, auth])

  /* ---- action runner ---- */
  const run = useCallback(
    async (fn: () => Promise<Workspace>, success?: string): Promise<boolean> => {
      setBusy(true)
      try {
        setWs(await fn())
        if (success) flash(success)
        return true
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          setAuth(null)
          flash('Your session expired — please sign in again.')
        } else {
          flash(err instanceof Error ? err.message : 'Something went wrong.')
        }
        return false
      } finally {
        setBusy(false)
      }
    },
    [flash],
  )

  /* ---- lookups ---- */
  const proj = useCallback<AppValue['proj']>((id) => ws?.projects.find((p) => p.id === id) ?? null, [ws])
  const client = useCallback<AppValue['client']>((id) => ws?.clients.find((c) => c.id === id) ?? null, [ws])
  const member = useCallback<AppValue['member']>((id) => ws?.team.find((m) => m.id === id) ?? null, [ws])
  const myWeek = useCallback<AppValue['myWeek']>(
    (offset) => weekKeys(offset, ws?.settings.weekStart ?? 'Monday'),
    [ws?.settings.weekStart],
  )

  /* ------------------------------------------------------------------ */
  /* session                                                             */
  /* ------------------------------------------------------------------ */

  const afterAuth = useCallback((payload: AuthPayload) => {
    setAuth(payload)
    setScreen('timer')
    setDraftState(EMPTY_DRAFT)
    const first = payload.workspaces[0]?.id ?? null
    setWsId((current) => (current && payload.workspaces.some((w) => w.id === current) ? current : first))
  }, [])

  const signIn = useCallback<AppValue['signIn']>(
    async (email, password) => {
      try {
        afterAuth(await api.login({ email, password }))
        setMfaPending(false)
        return null
      } catch (err) {
        // The password was right; the session is just still aal1. Surface a
        // code prompt rather than an error -- nothing has gone wrong.
        if (err instanceof ApiError && err.message === MFA_REQUIRED) {
          setMfaPending(true)
          return null
        }
        return err instanceof Error ? err.message : 'Could not sign in.'
      }
    },
    [afterAuth],
  )

  const submitMfaCode = useCallback<AppValue['submitMfaCode']>(
    async (code) => {
      try {
        await api.mfa.verifyPending(code)
        afterAuth(await api.me())
        setMfaPending(false)
        return null
      } catch (err) {
        return err instanceof Error ? err.message : 'That code was not accepted.'
      }
    },
    [afterAuth],
  )

  const signUp = useCallback<AppValue['signUp']>(
    async (input) => {
      try {
        afterAuth(await api.register(input))
        return null
      } catch (err) {
        return err instanceof Error ? err.message : 'Could not create the account.'
      }
    },
    [afterAuth],
  )

  /**
   * Always resolves the same way whether or not the address exists -- the
   * form must not become a way to find out who has an account.
   */
  const resetPassword = useCallback(async (email: string) => {
    try {
      const res = await api.resetPassword(email)
      return res.ok ? null : res.problem
    } catch (e) {
      // A thrown error is the network, not the address. Still worth saying:
      // the alternative is a screen that claims to have sent something.
      return e instanceof Error ? e.message : 'The email could not be sent.'
    }
  }, [])

  const signOut = useCallback(async () => {
    try {
      await api.logout()
    } catch {
      /* the cookie is gone either way */
    }
    setAuth(null)
    setWs(null)
    setWsId(null)
    setModal(null)
    setScreen('timer')
    setDraftState(EMPTY_DRAFT)
  }, [])

  const selectWorkspace = useCallback((id: ID) => {
    setWsId(id)
    setWs(null)
    setScreen('timer')
    setDraftState(EMPTY_DRAFT)
  }, [])

  const enterNew = useCallback(
    async (fn: () => Promise<Workspace>): Promise<string | null> => {
      try {
        const snap = await fn()
        setWs(snap)
        setWsId(snap.id)
        setScreen('timer')
        setDraftState(EMPTY_DRAFT)
        await refreshAuth()
        return null
      } catch (err) {
        return err instanceof Error ? err.message : 'Something went wrong.'
      }
    },
    [refreshAuth],
  )

  const createWorkspace = useCallback(
    (name: string) => enterNew(() => api.createWorkspace(name)),
    [enterNew],
  )
  const joinWorkspace = useCallback((code: string) => enterNew(() => api.joinWorkspace(code)), [enterNew])
  const acceptInvite = useCallback((id: ID) => enterNew(() => api.acceptInvite(id)), [enterNew])

  /* ------------------------------------------------------------------ */
  /* timer                                                               */
  /* ------------------------------------------------------------------ */

  const id = ws?.id ?? ''

  const stopTimerInner = useCallback(
    async (endedAt?: string) => {
      if (!ws || !running) return false

      /*
       * Rounding applies only to an ordinary stop. An explicit end is already
       * a deliberate instant -- the midnight rule and the ten-hour cap both
       * pass one -- and rounding it would move a boundary that was chosen on
       * purpose.
       *
       * Capped at the session limit here as well as server-side, so rounding
       * up can never push a stop past it.
       */
      let end = endedAt
      if (end === undefined && ws.settings.roundToMinute) {
        const elapsed = Math.max(0, Math.round((Date.now() - running.at) / 1000))
        const rounded = Math.min(roundToMinute(elapsed), MAX_SESSION_SEC)
        end = new Date(running.at + rounded * 1000).toISOString()
      }

      const res = await run(() => api.stopTimer(ws.id, end))

      /*
       * Back to the default, so the next entry starts from nothing rather
       * than silently inheriting this one -- which is how time ends up on
       * the wrong matter. The client picker beside it is screen state; the
       * Timer screen clears that alongside.
       *
       * Billable included. It used to be kept, so one non-billable entry
       * quietly made every later one non-billable too: the $ stayed off
       * after the timer stopped, and nothing on screen said the next hour
       * was being written off as well. Not charging is the silent mistake --
       * the hours still look logged and the money is simply never asked for
       * -- so it has to be chosen each time rather than inherited. Choosing
       * a project still sets it from the project, and continuing an entry
       * still copies that entry's.
       */
      if (res) setDraftState(freshDraft())
      return res
    },
    [ws, running, run, freshDraft],
  )

  const act: Actions = useMemo(
    () => ({
      startTimer: (fixedAmount = null, projectId) =>
        run(() =>
          api.startTimer(id, {
            desc: draft.desc,
            projectId: projectId === undefined ? draft.projectId : projectId,
            task: draft.task,
            billable: draft.billable,
            at: Date.now(),
            fixedAmount,
          }),
        ),
      stopTimer: () => stopTimerInner(),
      stopTimerFor: (userId, name) =>
        run(() => api.stopTimerFor(id, userId), `Stopped ${name}'s timer`),
      updateTimer: (patch) => run(() => api.updateTimer(id, patch)),

      addManual: ({ start, end, day, fixedAmount = null, projectId }) => {
        const dur = (end - start + DAY_SEC) % DAY_SEC
        return run(
          () =>
            api.createEntry(id, {
              desc: draft.desc,
              projectId: projectId === undefined ? draft.projectId : projectId,
              task: draft.task,
              billable: draft.billable,
              day,
              start,
              dur,
              fixedAmount,
            }),
          `Added ${Math.floor(dur / 3600)}h ${String(Math.round((dur % 3600) / 60)).padStart(2, '0')}m`,
        ).then((ok) => {
          // Same reset as stopping a timer, and for the same reasons: a
          // finished entry should leave nothing behind, least of all its
          // billable flag.
          if (ok) setDraftState(freshDraft())
          return ok
        })
      },

      continueEntry: async (e) => {
        if (running) await stopTimerInner()
        setDraftState((d) => ({ ...d, desc: e.desc, projectId: e.projectId, task: e.task, billable: e.billable }))
        setScreen('timer')
        return run(
          () =>
            api.startTimer(id, {
              desc: e.desc,
              projectId: e.projectId,
              task: e.task,
              billable: e.billable,
              at: Date.now(),
            }),
          'Timer started',
        )
      },

      createEntry: (e) => run(() => api.createEntry(id, e), 'Entry added'),
      updateEntry: (entryId, patch) => run(() => api.updateEntry(id, entryId, patch)),
      deleteEntry: (entryId) => run(() => api.deleteEntry(id, entryId), 'Entry deleted'),
      duplicateEntry: (e) =>
        run(
          () =>
            api.createEntry(id, {
              desc: e.desc,
              projectId: e.projectId,
              task: e.task,
              tags: e.tags,
              billable: e.billable,
              day: e.day,
              start: e.start,
              dur: e.dur,
              userId: e.userId,
            }),
          'Entry duplicated',
        ),
      wipeEntries: (scope) =>
        run(() => api.wipeEntries(id, scope), scope === 'all' ? 'All time entries deleted' : 'Your time entries deleted'),

      setCell: (projectId, day, seconds) => run(() => api.setCell(id, { projectId, day, seconds })),
      clearRow: (projectId, days) => run(() => api.clearRow(id, { projectId, days }), 'Row cleared'),
      copyWeek: async (from, to) => {
        setBusy(true)
        try {
          const snap = await api.copyWeek(id, { from, to })
          setWs(snap)
          flash(`Copied ${snap.copied} ${snap.copied === 1 ? 'entry' : 'entries'} forward`)
          return true
        } catch (err) {
          flash(err instanceof Error ? err.message : 'Could not copy the week.')
          return false
        } finally {
          setBusy(false)
        }
      },
      submitWeek: (weekStart) => run(() => api.submitWeek(id, weekStart), 'Week submitted for approval'),
      unsubmitWeek: (weekStart) => run(() => api.unsubmitWeek(id, weekStart), 'Week reopened for editing'),

      createProject: (b) => run(() => api.createProject(id, b), 'Project created'),
      updateProject: (projectId, b) => run(() => api.updateProject(id, projectId, b)),
      deleteProject: (projectId) => run(() => api.deleteProject(id, projectId), 'Project deleted'),

      createClient: (b) => run(() => api.createClient(id, b), 'Client added'),
      updateClient: (clientId, b) => run(() => api.updateClient(id, clientId, b)),
      binClient: (clientId) => run(() => api.binClient(id, clientId), 'Moved to deleted clients'),
      restoreClient: (clientId, name) =>
        run(() => api.restoreClient(id, clientId, name), 'Client restored'),
      purgeClient: (clientId) => run(() => api.purgeClient(id, clientId), 'Client deleted for good'),

      invite: async (b) => {
        setBusy(true)
        try {
          const { ws: fresh, actionLink, emailed, emailProblem, linkProblem } = await api.invite(id, b)
          setWs(fresh)
          flash(emailed ? 'Invite sent' : 'Added — send them the link')
          return { ok: true, actionLink, emailed, emailProblem, problem: null, linkProblem }
        } catch (err) {
          const problem = err instanceof Error ? err.message : 'That did not work'
          flash(problem)
          return { ok: false, actionLink: null, emailed: false, emailProblem: null, problem, linkProblem: null }
        } finally {
          setBusy(false)
        }
      },
      cancelInvite: (inviteId) => run(() => api.cancelInvite(id, inviteId), 'Invite cancelled'),
      updateMember: (userId, b) => run(() => api.updateMember(id, userId, b)),
      removeMember: (userId) => run(() => api.removeMember(id, userId), 'Person removed'),
      restoreMember: (userId) => run(() => api.restoreMember(id, userId), 'Person restored'),

      resetLinkFor: async (userId) => {
        try {
          const { url } = await api.resetLinkFor(id, userId)
          return url
        } catch (e) {
          flash(e instanceof Error ? e.message : 'Could not create a link.')
          return null
        }
      },

      updateWorkspace: (b) => run(() => api.updateWorkspace(id, b)),
      updateAppearance: (b) => run(() => api.updateAppearance(id, b)),
      regenerateCode: () => run(() => api.regenerateCode(id), 'New join code generated'),
      deleteWorkspace: async () => {
        try {
          await api.deleteWorkspace(id)
        } catch (err) {
          flash(err instanceof Error ? err.message : 'Could not delete the workspace.')
          return false
        }
        setWs(null)
        setWsId(null)
        await refreshAuth()
        flash('Workspace deleted')
        return true
      },
      leaveWorkspace: async () => {
        try {
          await api.leaveWorkspace(id)
        } catch (err) {
          flash(err instanceof Error ? err.message : 'Could not leave the workspace.')
          return false
        }
        setWs(null)
        setWsId(null)
        await refreshAuth()
        flash('You left the workspace')
        return true
      },

      // Not through run(): the function answers with a report, not a
      // snapshot, so the workspace is refetched only once something changed.
      importCsv: async (q) => {
        const report = await api.importCsv(id, q)
        if (q.mode === 'commit') setWs(await api.workspace(id))
        return report
      },

      addTag: (name) => run(() => api.addTag(id, name)),
      removeTag: (name) => run(() => api.removeTag(id, name), 'Tag removed'),

      // The export runs in an Edge Function that checks admin rights, so it
      // is a POST whose body becomes the download rather than a plain link.
      backupWorkspace: async (scope = 'standard') => {
        try {
          const { filename, json } = await api.backupWorkspace(id, scope)
          downloadFile(filename, json, 'application/json')
          flash('Backup downloaded')
          return true
        } catch (err) {
          flash(err instanceof Error ? err.message : 'Could not take a backup.')
          return false
        }
      },

      restoreWorkspace: async (file) => {
        try {
          const report = await api.restoreWorkspace(id, file)
          // The snapshot is stale the moment anything lands, and a restore can
          // touch every table -- so it is refetched rather than patched.
          setWs(await api.workspace(id))
          return report
        } catch (err) {
          flash(err instanceof Error ? err.message : 'Could not restore that backup.')
          return null
        }
      },

      exportCsv: (from, to, scope, filters) => {
        void api
          .exportCsv(id, { from, to, scope, ...filters })
          .then(() => flash('Export downloaded'))
          .catch((err: unknown) => flash(err instanceof Error ? err.message : 'Export failed'))
      },
    }),
    [id, draft, run, running, stopTimerInner, flash, refreshAuth, freshDraft],
  )

  /* ---- a reset or invite link signs you in mid-flow ---- */
  useEffect(() => {
    const { data } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') setRecovery(true)
      if (event === 'SIGNED_OUT') setRecovery(false)
    })
    return () => data.subscription.unsubscribe()
  }, [])

  /* ---- auto-stop at midnight ---- */
  /*
   * Its own interval, rather than riding the app-wide clock.
   *
   * This only has to notice that the date has changed, which a check twice a
   * minute catches just as well as one every second -- and depending on the
   * shared tick meant this provider re-rendered once a second forever, taking
   * every screen in the app with it.
   */
  const autoStopping = useRef(false)
  useEffect(() => {
    if (!running || !ws?.settings.autoStop) return

    const check = () => {
      if (autoStopping.current) return
      if (dayKey(new Date(running.at)) === todayKey()) return
      // Past the hard limit the cap below owns this timer. Midnight at the end
      // of the start day can be up to 23 hours after it began, which the limit
      // refuses -- the server would clamp it anyway, but then the toast would
      // name the wrong reason.
      if (Date.now() - running.at >= MAX_SESSION_SEC * 1000) return
      autoStopping.current = true
      // Midnight at the end of the day the timer started on, so the entry
      // lands on the boundary rather than on whenever this check ran.
      void stopTimerInner(instantOf(dayKey(new Date(running.at)), DAY_SEC)).then(() => {
        autoStopping.current = false
        flash('Timer stopped at midnight, as set in workspace rules.')
      })
    }

    check()
    const t = setInterval(check, 30_000)
    return () => clearInterval(t)
  }, [running, ws?.settings.autoStop, stopTimerInner, flash])

  /*
   * The ten-hour limit, which is a rule rather than a preference.
   *
   * Unconditional: autoStop above is something a workspace turns on, this is
   * what the database will accept. Before it existed, a timer left running
   * past the limit could not be stopped at all -- the stop wrote now(), the
   * check constraint refused the row, and the hours sat in a timer that could
   * neither be saved nor discarded.
   *
   * The end time is the limit, not the moment this noticed. The browser may
   * have been closed for days, so stop_timer caps server-side too; this exists
   * so somebody watching the screen is told, rather than finding a ten-hour
   * entry later and wondering.
   */
  const cappingSession = useRef(false)
  useEffect(() => {
    if (!running) return

    const check = () => {
      if (cappingSession.current) return
      if (Date.now() - running.at < MAX_SESSION_SEC * 1000) return
      cappingSession.current = true
      void stopTimerInner(new Date(running.at + MAX_SESSION_SEC * 1000).toISOString()).then(() => {
        cappingSession.current = false
        flash('Timer reached the 10 hour limit. It was stopped and the time saved.')
      })
    }

    check()
    const t = setInterval(check, 30_000)
    return () => clearInterval(t)
  }, [running, stopTimerInner, flash])

  /* ---- Escape closes the modal ---- */
  useEffect(() => {
    if (!modal) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setModal(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [modal])

  const value: AppValue = {
    booting,
    auth,
    user: auth?.user ?? null,
    authConfig,
    signIn,
    signUp,
    signOut,
    refreshAuth,
    mfaPending,
    submitMfaCode,
    resetPassword,
    recovery,
    wsId,
    selectWorkspace,
    createWorkspace,
    joinWorkspace,
    acceptInvite,
    ws,
    me,
    role,
    canManage,
    canSeeAll,
    canSetRates,
    running,
    loadError,
    screen,
    setScreen,
    draft,
    setDraft,
    manualMode,
    setManualMode,
    modal,
    setModal,
    confirm,
    toast,
    flash,
    busy,
    proj,
    client,
    member,
    myWeek,
    act,
  }

  return (
    <Ctx.Provider value={value}>
      <NowProvider>{children}</NowProvider>
    </Ctx.Provider>
  )
}
